import { useCallback, useEffect, useRef, useState } from 'react';
import { MUHASEBE_AD, api, formatTarih, formatTl } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * RESMÎ BELGE — panelden e-Fatura / e-Arşiv ve irsaliye (docs/RESMI-BELGE.md).
 *
 * Kullanıcı muhasebe programına gitmez: Teklif → Sipariş → (İrsaliye) → Fatura kaydı →
 * Resmîleştir → PDF adımlarının hepsi burada. Resmî belge GERİ ALINAMAZ; gönderim
 * yalnız onay penceresinden, onay kutusu işaretlenince yapılır.
 */
export type ResmiDurum = {
  durum: 'YOK' | 'GONDERILIYOR' | 'RESMI' | 'HATA';
  tur: 'E_FATURA' | 'E_ARSIV' | null; turAd: string | null;
  no: string | null; hata: string | null; at: string | null; pdfVar: boolean;
};
export type IrsaliyeDurum = { durum: 'YOK' | 'ACILIYOR' | 'BELIRSIZ' | 'VAR'; no: string | null; at: string | null };
type Ayar = { acik: boolean; degistirebilir: boolean; kip: string };

const mesaj = (e: unknown, yedek: string) => (e instanceof Error ? e.message : yedek);

/** Liste ve detayda aynı rozet: Gönderiliyor… / Resmî · numara / Hata · neden. */
export function ResmiRozet({ r, kisa }: { r?: ResmiDurum | null; kisa?: boolean }) {
  if (!r || r.durum === 'YOK') return null;
  if (r.durum === 'GONDERILIYOR') return <span className="ccd-badge ccd-badge-warning" title="Resmî belge gönderildi, sonucu bekleniyor">gönderiliyor…</span>;
  if (r.durum === 'HATA') {
    return <span className="ccd-badge ccd-badge-danger" title={r.hata ?? 'Resmî belge gönderilemedi'}>{kisa ? 'resmî belge hatası' : `hata · ${r.hata ?? 'neden bildirilmedi'}`}</span>;
  }
  return (
    <span className="ccd-badge ccd-badge-success" title={`${r.turAd ?? 'Resmî belge'}${r.at ? ` · ${formatTarih(r.at)}` : ''}`}>
      {kisa ? 'resmî' : (r.turAd ?? 'resmî')}{r.no ? ` · ${r.no}` : ''}
    </span>
  );
}

function useAyar() {
  const [ayar, setAyar] = useState<Ayar | null>(null);
  const yukle = useCallback(() => { api<Ayar>('/api/resmi-belge/ayar').then(setAyar).catch(() => setAyar(null)); }, []);
  useEffect(yukle, [yukle]);
  return { ayar, yukle };
}

/* ─── Ayarlar ekranı: aç / kapa anahtarı (yalnız yönetici) ───────────────── */
export function ResmiBelgeAyar() {
  const { ayar, yukle } = useAyar();
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);
  if (!ayar) return null;

  async function degistir() {
    const hedef = !ayar!.acik;
    if (hedef && !window.confirm(
      `Resmî belge kesimi AÇILSIN mı?\n\nAçıkken yönetici ve muhasebe kullanıcıları panelden e-Fatura / e-Arşiv fatura kesebilir. Kesilen belge geri alınamaz.\n\nAçmadan önce ${MUHASEBE_AD}'te e-Fatura / e-Arşiv'in etkin, seri ve numara ayarının doğru olduğundan emin olun.`)) return;
    setBekliyor(true); setHata(null);
    try { await api('/api/resmi-belge/ayar', { method: 'PUT', body: JSON.stringify({ acik: hedef }) }); yukle(); }
    catch (e) { setHata(mesaj(e, 'Ayar değiştirilemedi')); }
    finally { setBekliyor(false); }
  }

  return (
    <div className="ccd-card ccd-stack-sm" data-tur="resmi-belge-ayar">
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div style={{ minWidth: 0, flex: '1 1 320px' }}>
          <div className="ccd-fieldset-title">Resmî belge kesimi (e-Fatura / e-Arşiv, irsaliye)</div>
          <div className="ccd-meta">
            Açıkken sipariş detayından fatura kaydı resmîleştirilir ve irsaliye açılır; kullanıcı {MUHASEBE_AD}'e gitmez.
            Kesilen resmî belge <b>geri alınamaz</b>. Kapalıyken yalnız önizleme çalışır, hiçbir şey gönderilmez.
            Kesebilenler: yönetici ve muhasebe.
          </div>
        </div>
        <div className="ccd-row" style={{ gap: 10 }}>
          <span className={`ccd-badge${ayar.acik ? ' ccd-badge-success' : ''}`}>{ayar.acik ? 'açık' : 'kapalı'}</span>
          <button type="button" role="switch" aria-checked={ayar.acik} aria-label="Resmî belge kesimi"
                  className={`ccd-switch${ayar.acik ? ' ccd-is-on' : ''}`} disabled={!ayar.degistirebilir || bekliyor}
                  title={ayar.degistirebilir ? (ayar.acik ? 'Kapat' : 'Aç (onay sorulur)') : 'Yalnız yönetici açıp kapatabilir'}
                  onClick={() => void degistir()} />
        </div>
      </div>
      {!ayar.degistirebilir && <div className="ccd-meta">Bu anahtarı yalnız yönetici değiştirebilir.</div>}
      {ayar.kip !== 'real' && <div className="ccd-meta">Deneme kipi: gönderimler gerçek {MUHASEBE_AD} hesabına gitmez.</div>}
      {hata && <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon">!</span><span>{hata}</span></div>}
    </div>
  );
}

/* ─── Onay penceresi: resmîleştir ────────────────────────────────────────── */
type ResmiOnizleme = {
  siparis: string; cari: string; kip: string; acik: boolean; durum: string; sonHata: string | null;
  faturaKaydiVar: boolean; faturaNo: string | null;
  tur: 'E_FATURA' | 'E_ARSIV' | null; turAd: string | null; senaryo: 'TEMEL' | 'TICARI' | null;
  alici: { ad: string; vergiNo: string | null; vergiDairesi: string | null; adres: string | null; gelenKutusu: string | null } | null;
  tutar: { haricKurus: number; kdvKurus: number; dahilKurus: number } | null;
  sifirKdvVar: boolean; mevcutBelge: { no: string | null; turAd: string } | null;
  onayMetni: string | null; engeller: string[]; uyarilar: string[];
};

function ResmiCekmece({ siparisId, kapat, gonderildi }: { siparisId: number; kapat: () => void; gonderildi: (m: string) => void }) {
  const [v, setV] = useState<ResmiOnizleme | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(true);
  const [onay, setOnay] = useState(false);
  const [senaryo, setSenaryo] = useState<'TEMEL' | 'TICARI'>('TEMEL');
  const [muafKod, setMuafKod] = useState('');
  const [muafAciklama, setMuafAciklama] = useState('');

  const govde = useCallback(() => ({
    senaryo,
    ...(muafKod.trim() ? { kdvMuafiyetKodu: muafKod.trim() } : {}),
    ...(muafAciklama.trim() ? { kdvMuafiyetAciklama: muafAciklama.trim() } : {}),
  }), [senaryo, muafKod, muafAciklama]);

  const onizle = useCallback(async () => {
    setBekliyor(true); setHata(null); setOnay(false);
    try { setV(await api<ResmiOnizleme>(`/api/siparis/${siparisId}/resmilestir`, { method: 'POST', body: JSON.stringify(govde()) })); }
    catch (e) { setHata(mesaj(e, 'Önizleme alınamadı')); }
    finally { setBekliyor(false); }
  }, [siparisId, govde]);
  // Yalnız açılışta ve senaryo değişince; muafiyet alanları "Yeniden denetle" ile gönderilir.
  useEffect(() => { void onizle(); }, [siparisId, senaryo]);   // eslint-disable-line react-hooks/exhaustive-deps

  async function gonder(esitle: boolean) {
    if (!v) return;
    setBekliyor(true); setHata(null);
    try {
      const r = await api<{ mesaj?: string; turAd?: string }>(`/api/siparis/${siparisId}/resmilestir?uygula=1`, {
        method: 'POST', body: JSON.stringify(esitle ? {} : { ...govde(), onay: v.onayMetni }),
      });
      gonderildi(r.mesaj ?? `${r.turAd ?? 'Resmî belge'} gönderildi; sonucu birkaç saniye içinde burada görünecek.`);
    } catch (e) { setHata(mesaj(e, 'Gönderilemedi')); setBekliyor(false); void onizle(); }
  }

  const gonderilebilir = v != null && v.engeller.length === 0 && v.acik && v.tur != null && v.tutar != null;
  return (
    <div className="ccd-drawer-arka" onClick={() => !bekliyor && kapat()} role="presentation">
      <aside className="ccd-drawer" style={{ width: 'min(640px, 100%)' }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Resmîleştir">
        <div className="ccd-stack">
          <div className="ccd-row-between">
            <div><div className="ccd-eyebrow">Fatura kaydı → Resmî belge</div><h2 className="ccd-heading">Resmîleştir</h2></div>
            <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat} disabled={bekliyor}>Kapat</button>
          </div>
          {hata && <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon">!</span><span>{hata}</span></div>}
          {!v ? (bekliyor && <div className="ccd-inline-note">Alıcı ve fatura bilgileri denetleniyor…</div>) : (
            <>
              <div className="ccd-meta">
                <b>{v.cari}</b> · {v.siparis}
                {v.kip !== 'real' && <span className="ccd-badge" style={{ marginLeft: 8 }}>deneme kipi — gerçek {MUHASEBE_AD}'e gitmez</span>}
              </div>
              {!v.acik && (
                <div className="ccd-banner"><span className="ccd-banner-icon">i</span>
                  <span>Resmî belge kesimi kapalı — Ayarlar'dan açılır. Aşağıdaki yalnız önizlemedir; hiçbir şey gönderilmez.</span></div>
              )}
              {v.sonHata && v.durum === 'HATA' && (
                <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon">!</span>
                  <div><b>Önceki gönderim başarısız oldu.</b> <span>{v.sonHata}</span><div>Nedeni düzeltip yeniden gönderebilirsiniz; panel kendiliğinden yeniden denemez.</div></div></div>
              )}
              {v.engeller.length > 0 && (
                <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon">!</span>
                  <div><b>Belge henüz kesilemez.</b>{v.engeller.map((e, n) => <div key={n}>• {e}</div>)}</div></div>
              )}
              {v.uyarilar.map((u, n) => (
                <div key={n} className="ccd-banner"><span className="ccd-banner-icon">i</span><span>{u}</span></div>
              ))}

              {v.alici && v.tutar && (
                <dl className="ccd-belge-ozet">
                  <dt>Belge türü</dt>
                  <dd><b>{v.turAd ?? 'belirlenemedi'}</b>
                    <div className="ccd-meta">{v.tur === 'E_FATURA' ? 'Alıcı e-Fatura mükellefi; belge alıcının gelen kutusuna gider.'
                      : v.tur === 'E_ARSIV' ? 'Alıcı e-Fatura mükellefi değil; e-Arşiv fatura düzenlenir.' : 'Vergi numarası olmadan belge türü belirlenemez.'}</div></dd>
                  <dt>Alıcı</dt>
                  <dd><b>{v.alici.ad}</b>
                    <div className="ccd-meta">{v.alici.vergiNo ? `Vergi / T.C. no ${v.alici.vergiNo}` : 'vergi no yok'}{v.alici.vergiDairesi ? ` · ${v.alici.vergiDairesi}` : ''}</div>
                    <div className="ccd-meta">{v.alici.adres ?? 'adres yok'}</div>
                    {v.alici.gelenKutusu && <div className="ccd-meta ccd-mono">{v.alici.gelenKutusu}</div>}</dd>
                  <dt>Tutar</dt>
                  <dd><b className="ccd-belge-tutar">{formatTl(v.tutar.dahilKurus)}</b>
                    <div className="ccd-meta">KDV hariç {formatTl(v.tutar.haricKurus)} · KDV {formatTl(v.tutar.kdvKurus)}</div></dd>
                </dl>
              )}

              {v.tur === 'E_FATURA' && v.engeller.length === 0 && (
                <div className="ccd-field">
                  <label className="ccd-field-label" htmlFor="rb-senaryo">Senaryo</label>
                  <select id="rb-senaryo" className="ccd-select" value={senaryo} disabled={bekliyor} onChange={(e) => setSenaryo(e.target.value as 'TEMEL' | 'TICARI')}>
                    <option value="TEMEL">Temel fatura — alıcı sistem üzerinden reddedemez</option>
                    <option value="TICARI">Ticari fatura — alıcı 8 gün içinde kabul / ret verebilir</option>
                  </select>
                </div>
              )}
              {v.sifirKdvVar && (
                <div className="ccd-stack-sm">
                  <div className="ccd-field">
                    <label className="ccd-field-label" htmlFor="rb-muaf">KDV muafiyet kodu<span className="ccd-required"> *</span></label>
                    <input id="rb-muaf" className="ccd-input" inputMode="numeric" maxLength={3} value={muafKod} placeholder="Örn. 350" onChange={(e) => setMuafKod(e.target.value)} />
                    <div className="ccd-field-hint">Faturada %0 KDV'li kalem var. Kodu mali müşavirinizden öğrenin (GİB muafiyet kod listesi).</div>
                  </div>
                  <div className="ccd-field">
                    <label className="ccd-field-label" htmlFor="rb-muaf-a">Muafiyet açıklaması</label>
                    <input id="rb-muaf-a" className="ccd-input" maxLength={250} value={muafAciklama} placeholder="Kod 250 ya da 350 ise zorunlu" onChange={(e) => setMuafAciklama(e.target.value)} />
                  </div>
                  <button type="button" className="ccd-btn ccd-btn-sm" style={{ alignSelf: 'flex-start' }} disabled={bekliyor} onClick={() => void onizle()}>Yeniden denetle</button>
                </div>
              )}

              {v.mevcutBelge ? (
                <button className="ccd-btn ccd-btn-primary" disabled={bekliyor || !v.acik}
                        title={v.acik ? 'Yeni belge kesilmez; yalnız paneldeki durum güncellenir' : 'Resmî belge kesimi kapalı — Ayarlar\'dan açılır'}
                        onClick={() => void gonder(true)}>Paneldeki durumu güncelle{v.mevcutBelge.no ? ` (${v.mevcutBelge.no})` : ''}</button>
              ) : (
                <>
                  <div className="ccd-belge-uyari" role="alert">
                    <b>Bu işlem resmî belge düzenler ve geri alınamaz.</b>
                    <div>Belge Gelir İdaresi'ne bildirilir ve alıcıya iletilir. Yanlış kesilen belge panelden düzeltilemez; iptal ya da iade faturası gerekir.</div>
                    <label className="ccd-belge-onay">
                      <input type="checkbox" checked={onay} disabled={!gonderilebilir || bekliyor} onChange={(e) => setOnay(e.target.checked)} />
                      <span>
                        {gonderilebilir
                          ? <>Alıcıyı, belge türünü ve tutarı kontrol ettim: <b>{v.alici!.ad}</b> adına <b>{formatTl(v.tutar!.dahilKurus)}</b> tutarında <b>{v.turAd}</b> kesilsin.</>
                          : 'Yukarıdaki eksikler tamamlanınca onaylanabilir.'}
                      </span>
                    </label>
                  </div>
                  <button className="ccd-btn ccd-btn-primary" disabled={!gonderilebilir || !onay || bekliyor}
                          title={!v.acik ? 'Resmî belge kesimi kapalı — Ayarlar\'dan açılır' : v.engeller.length > 0 ? v.engeller[0] : !onay ? 'Önce onay kutusunu işaretleyin' : ''}
                          onClick={() => void gonder(false)}>
                    {bekliyor ? 'Gönderiliyor…' : `${v.turAd ?? 'Resmî belge'} kes`}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

/* ─── İrsaliye penceresi ─────────────────────────────────────────────────── */
type IrsaliyeOnizleme = {
  siparis: string; cari: string; kip: string; acik: boolean; sevkTarihi: string; adres: string | null; il: string | null; ilce: string | null;
  satirlar: { kod: string; ad: string; miktar: number; birim: string | null; disUrun: string | null }[];
  engeller: string[]; not: string;
};

function IrsaliyeCekmece({ siparisId, kapat, acildi }: { siparisId: number; kapat: () => void; acildi: (m: string) => void }) {
  const [v, setV] = useState<IrsaliyeOnizleme | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(true);
  const [sevk, setSevk] = useState('');
  const [adres, setAdres] = useState('');
  const [il, setIl] = useState('');
  const [ilce, setIlce] = useState('');
  const ilk = useRef(true);

  const govde = () => ({
    ...(sevk ? { sevkTarihi: sevk } : {}), ...(adres.trim() ? { adres: adres.trim() } : {}),
    ...(il.trim() ? { il: il.trim() } : {}), ...(ilce.trim() ? { ilce: ilce.trim() } : {}),
  });
  async function onizle() {
    setBekliyor(true); setHata(null);
    try {
      const r = await api<IrsaliyeOnizleme>(`/api/siparis/${siparisId}/irsaliye`, { method: 'POST', body: JSON.stringify(ilk.current ? {} : govde()) });
      setV(r);
      if (ilk.current) { ilk.current = false; setSevk(r.sevkTarihi); setAdres(r.adres ?? ''); setIl(r.il ?? ''); setIlce(r.ilce ?? ''); }
    } catch (e) { setHata(mesaj(e, 'Önizleme alınamadı')); }
    finally { setBekliyor(false); }
  }
  useEffect(() => { void onizle(); }, [siparisId]);   // eslint-disable-line react-hooks/exhaustive-deps

  async function ac() {
    if (!v || !window.confirm(`${v.cari} adına ${v.satirlar.length} kalemlik irsaliye kaydı açılsın mı?`)) return;
    setBekliyor(true); setHata(null);
    try {
      const r = await api<{ mesaj?: string }>(`/api/siparis/${siparisId}/irsaliye?uygula=1`, { method: 'POST', body: JSON.stringify(govde()) });
      acildi(r.mesaj ?? 'İrsaliye kaydı açıldı.');
    } catch (e) { setHata(mesaj(e, 'İrsaliye açılamadı')); setBekliyor(false); }
  }

  return (
    <div className="ccd-drawer-arka" onClick={() => !bekliyor && kapat()} role="presentation">
      <aside className="ccd-drawer" style={{ width: 'min(640px, 100%)' }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="İrsaliye oluştur">
        <div className="ccd-stack">
          <div className="ccd-row-between">
            <div><div className="ccd-eyebrow">Sipariş → İrsaliye</div><h2 className="ccd-heading">İrsaliye oluştur</h2></div>
            <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat} disabled={bekliyor}>Kapat</button>
          </div>
          {hata && <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon">!</span><span>{hata}</span></div>}
          {!v ? (bekliyor && <div className="ccd-inline-note">Hazırlanıyor…</div>) : (
            <>
              <div className="ccd-meta">
                <b>{v.cari}</b> · {v.siparis}
                {v.kip !== 'real' && <span className="ccd-badge" style={{ marginLeft: 8 }}>deneme kipi — gerçek {MUHASEBE_AD}'e gitmez</span>}
              </div>
              {!v.acik && (
                <div className="ccd-banner"><span className="ccd-banner-icon">i</span>
                  <span>Resmî belge kesimi kapalı — Ayarlar'dan açılır. Aşağıdaki yalnız önizlemedir.</span></div>
              )}
              {v.engeller.length > 0 && (
                <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon">!</span>
                  <div><b>İrsaliye henüz açılamaz.</b>{v.engeller.map((e, n) => <div key={n}>• {e}</div>)}</div></div>
              )}
              <div className="ccd-belge-form">
                <div className="ccd-field">
                  <label className="ccd-field-label" htmlFor="ir-sevk">Sevk tarihi<span className="ccd-required"> *</span></label>
                  <input id="ir-sevk" className="ccd-input" type="date" value={sevk} onChange={(e) => setSevk(e.target.value)} />
                </div>
                <div className="ccd-field">
                  <label className="ccd-field-label" htmlFor="ir-il">İl</label>
                  <input id="ir-il" className="ccd-input" value={il} maxLength={60} onChange={(e) => setIl(e.target.value)} />
                </div>
                <div className="ccd-field">
                  <label className="ccd-field-label" htmlFor="ir-ilce">İlçe</label>
                  <input id="ir-ilce" className="ccd-input" value={ilce} maxLength={60} onChange={(e) => setIlce(e.target.value)} />
                </div>
                <div className="ccd-field ccd-belge-form-genis">
                  <label className="ccd-field-label" htmlFor="ir-adres">Sevk adresi<span className="ccd-required"> *</span></label>
                  <textarea id="ir-adres" className="ccd-textarea" rows={2} maxLength={250} value={adres} onChange={(e) => setAdres(e.target.value)} placeholder="Malın teslim edileceği adres" />
                </div>
              </div>
              <div className="ccd-table-wrap">
                <table className="ccd-table">
                  <thead><tr><th className="ccd-th">Ürün</th><th className="ccd-th">{MUHASEBE_AD}'teki karşılığı</th><th className="ccd-th ccd-td-num">Miktar</th></tr></thead>
                  <tbody>
                    {v.satirlar.map((s, n) => (
                      <tr key={n} className="ccd-tr">
                        <td className="ccd-td"><span className="ccd-mono">{s.kod}</span><div className="ccd-meta">{s.ad}</div></td>
                        <td className="ccd-td">{s.disUrun ?? <span className="ccd-kpi-down">bağlı değil</span>}</td>
                        <td className="ccd-td ccd-td-num">{s.miktar}{s.birim ? ` ${s.birim.toLowerCase()}` : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="ccd-banner"><span className="ccd-banner-icon">i</span><span>{v.not}</span></div>
              <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="ccd-btn ccd-btn-sm" disabled={bekliyor} onClick={() => void onizle()}>Yeniden denetle</button>
                <button className="ccd-btn ccd-btn-primary" disabled={bekliyor || v.engeller.length > 0 || !v.acik || !sevk || !adres.trim()}
                        title={!v.acik ? 'Resmî belge kesimi kapalı — Ayarlar\'dan açılır' : v.engeller[0] ?? (!adres.trim() ? 'Sevk adresini yazın' : '')}
                        onClick={() => void ac()}>{bekliyor ? 'Açılıyor…' : 'İrsaliye kaydını aç'}</button>
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

/* ─── Sipariş detayı: belge akışı kartı ──────────────────────────────────── */
type Adim = { ad: string; durum: 'tamam' | 'simdi' | 'bekliyor' | 'hata' | 'atlandi'; alt: React.ReactNode; eylem?: React.ReactNode };

export function BelgeAkisi({ siparis, yenile, faturalandir }: {
  siparis: {
    id: number; no: string; iptal: boolean;
    teklif: { no: string } | null;
    fatura: { kesildi: boolean; invoicedAt: string | null; no?: string };
    resmi: ResmiDurum; irsaliye: IrsaliyeDurum;
  };
  /** Sipariş detayını yeniden yükler (kayıt izi ve fatura kartı da tazelensin). */
  yenile: () => void;
  /** Mevcut "Faturalandır" penceresini açar; yetkisi yoksa verilmez. */
  faturalandir?: () => void;
}) {
  const { can } = useAuth();
  const kesebilir = can('invoice.issue');
  const { ayar } = useAyar();
  const [resmi, setResmi] = useState(siparis.resmi);
  const [pencere, setPencere] = useState<'resmi' | 'irsaliye' | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [sorguHatasi, setSorguHatasi] = useState<string | null>(null);
  const [pdfBekliyor, setPdfBekliyor] = useState(false);
  useEffect(() => { setResmi(siparis.resmi); }, [siparis.resmi]);

  // Gönderim sürerken durumu 4 sn'de bir sor. Sekme kapansa da işçi sonucu yazar.
  useEffect(() => {
    if (resmi.durum !== 'GONDERILIYOR') return;
    let iptal = false;
    const t = setInterval(() => {
      api<{ resmi: ResmiDurum; sorguHatasi: string | null }>(`/api/siparis/${siparis.id}/resmi-durum`).then((r) => {
        if (iptal) return;
        setSorguHatasi(r.sorguHatasi);
        if (r.resmi.durum !== 'GONDERILIYOR') {
          setResmi(r.resmi); yenile();
          setBilgi(r.resmi.durum === 'RESMI' ? `${r.resmi.turAd ?? 'Resmî belge'} kesildi${r.resmi.no ? ` · ${r.resmi.no}` : ''}.` : null);
        }
      }).catch(() => undefined);
    }, 4000);
    return () => { iptal = true; clearInterval(t); };
  }, [resmi.durum, siparis.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  async function pdfAc() {
    setPdfBekliyor(true); setHata(null);
    // Pencere tıklama anında açılır (sonradan açılan pencereyi tarayıcı engeller); adres gelince yönlendirilir.
    const w = ayar?.kip === 'real' ? window.open('', '_blank') : null;
    try {
      const r = await api<{ url: string; deneme: boolean }>(`/api/siparis/${siparis.id}/resmi-pdf`);
      if (r.deneme) { w?.close(); setBilgi('Deneme kipi: gerçek belge olmadığı için açılacak PDF yok.'); }
      else if (w) { w.opener = null; w.location.href = r.url; }
      else window.location.assign(r.url);
    } catch (e) { w?.close(); setHata(mesaj(e, 'PDF açılamadı')); }
    finally { setPdfBekliyor(false); }
  }
  async function kilitKaldir() {
    if (!window.confirm(`${MUHASEBE_AD}'te ${siparis.no} için irsaliye AÇILMADIĞINI kontrol ettiniz mi?\n\nAçıldıysa ve kilidi kaldırıp yeniden oluşturursanız irsaliye iki kez açılır.`)) return;
    setHata(null);
    try { await api(`/api/siparis/${siparis.id}/irsaliye-kilit-kaldir`, { method: 'POST' }); yenile(); }
    catch (e) { setHata(mesaj(e, 'Kilit kaldırılamadı')); }
  }

  const irs = siparis.irsaliye;
  const faturaVar = siparis.fatura.kesildi;
  /** Resmîleştir düğmesi neden pasif — kullanıcıya açıkça söylenir. */
  const resmiEngel = siparis.iptal ? 'Sipariş iptal edilmiş'
    : !faturaVar ? 'Önce fatura kaydı açılmalı (4. adım)'
    : !kesebilir ? 'Resmî belgeyi yalnız yönetici ve muhasebe kesebilir'
    : ayar && !ayar.acik ? 'Resmî belge kesimi kapalı — Ayarlar\'dan açılır'
    : null;

  const adimlar: Adim[] = [
    { ad: 'Teklif', durum: siparis.teklif ? 'tamam' : 'atlandi', alt: siparis.teklif ? siparis.teklif.no : 'tekliften açılmadı' },
    { ad: 'Sipariş', durum: 'tamam', alt: siparis.no },
    {
      ad: 'İrsaliye', durum: irs.durum === 'VAR' ? 'tamam' : irs.durum === 'BELIRSIZ' ? 'hata' : irs.durum === 'ACILIYOR' ? 'simdi' : faturaVar ? 'atlandi' : 'bekliyor',
      alt: irs.durum === 'VAR' ? <>kayıt açıldı{irs.at ? ` · ${formatTarih(irs.at)}` : ''}{irs.no ? ` · ${irs.no}` : ''}</>
        : irs.durum === 'ACILIYOR' ? 'açılıyor…'
        : irs.durum === 'BELIRSIZ' ? `İsteğin sonucu alınamadı. ${MUHASEBE_AD}'te irsaliyenin açılıp açılmadığını kontrol edin.`
        : faturaVar ? 'ayrı irsaliye açılmadı (fatura kaydı çıkışı içerir)' : 'isteğe bağlı · faturadan önce açılır',
      eylem: siparis.iptal || !kesebilir ? undefined
        : irs.durum === 'BELIRSIZ' ? <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => void kilitKaldir()}>Yeniden denemeye izin ver</button>
        : irs.durum === 'YOK' && !faturaVar && siparis.teklif ? <button className="ccd-btn ccd-btn-sm" onClick={() => setPencere('irsaliye')}>İrsaliye oluştur</button>
        : undefined,
    },
    {
      ad: 'Fatura kaydı', durum: faturaVar ? 'tamam' : 'bekliyor',
      alt: faturaVar ? <>açıldı{siparis.fatura.invoicedAt ? ` · ${formatTarih(siparis.fatura.invoicedAt)}` : ''}</> : siparis.teklif ? 'henüz açılmadı' : 'tekliften açılan siparişte açılır',
      eylem: !faturaVar && !siparis.iptal && siparis.teklif && faturalandir
        ? <button className="ccd-btn ccd-btn-sm" onClick={faturalandir}>Faturalandır</button> : undefined,
    },
    {
      ad: 'Resmîleştir', durum: resmi.durum === 'RESMI' ? 'tamam' : resmi.durum === 'HATA' ? 'hata' : resmi.durum === 'GONDERILIYOR' ? 'simdi' : 'bekliyor',
      alt: resmi.durum === 'YOK' ? (resmiEngel ?? 'e-Fatura ya da e-Arşiv; alıcıya göre panel belirler')
        : <><ResmiRozet r={resmi} />{resmi.durum === 'HATA' && <div>Nedeni düzeltip yeniden gönderin; panel kendiliğinden yeniden denemez.</div>}
            {resmi.durum === 'GONDERILIYOR' && <div>{sorguHatasi ?? 'Sonuç bekleniyor; sayfayı kapatabilirsiniz, durum kendiliğinden güncellenir.'}</div>}</>,
      eylem: resmi.durum === 'YOK' || resmi.durum === 'HATA'
        ? <button className="ccd-btn ccd-btn-primary ccd-btn-sm" disabled={resmiEngel != null} title={resmiEngel ?? 'Önizleme ve onay penceresini açar'}
                  onClick={() => { setBilgi(null); setPencere('resmi'); }}>{resmi.durum === 'HATA' ? 'Yeniden dene' : 'Resmîleştir'}</button>
        : undefined,
    },
    {
      ad: 'PDF', durum: resmi.durum === 'RESMI' ? 'tamam' : 'bekliyor',
      alt: resmi.durum === 'RESMI' ? 'resmî belgenin çıktısı' : 'belge resmîleşince açılır',
      eylem: resmi.pdfVar ? <button className="ccd-btn ccd-btn-sm" disabled={pdfBekliyor} onClick={() => void pdfAc()}>{pdfBekliyor ? 'Açılıyor…' : 'PDF\'i aç'}</button> : undefined,
    },
  ];

  return (
    <div className="ccd-card ccd-stack-sm" data-tur="belge-akisi">
      <div>
        <div className="ccd-fieldset-title">Fatura ve irsaliye</div>
        <div className="ccd-meta">Bütün adımlar panelden yapılır; {MUHASEBE_AD}'e geçmeniz gerekmez.</div>
      </div>
      {bilgi && <div className="ccd-banner"><span className="ccd-banner-icon">✓</span><span>{bilgi}</span></div>}
      {hata && <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon">!</span><span>{hata}</span></div>}
      <ol className="ccd-belge-adimlar">
        {adimlar.map((a, n) => (
          <li key={a.ad} className={`ccd-belge-adim ccd-belge-${a.durum}`}>
            <span className="ccd-belge-nokta" aria-hidden="true">{a.durum === 'tamam' ? '✓' : a.durum === 'hata' ? '!' : n + 1}</span>
            <div className="ccd-belge-govde">
              <div className="ccd-belge-ad">{a.ad}</div>
              <div className="ccd-meta">{a.alt}</div>
            </div>
            {a.eylem && <div className="ccd-belge-eylem">{a.eylem}</div>}
          </li>
        ))}
      </ol>
      <div className="ccd-meta">
        İrsaliyede panel <b>kaydı</b> açar. Firma e-İrsaliye mükellefiyse o belgenin resmîleştirilmesi bugün yalnız {MUHASEBE_AD} ekranından yapılabilir
        (programın dış bağlantısında bu işlem yok). İptal ve iade faturası da şimdilik {MUHASEBE_AD}'ten kesilir.
      </div>
      {pencere === 'resmi' && (
        <ResmiCekmece siparisId={siparis.id} kapat={() => setPencere(null)}
                      gonderildi={(m) => { setPencere(null); setBilgi(m); setResmi((r) => ({ ...r, durum: 'GONDERILIYOR', hata: null })); yenile(); }} />
      )}
      {pencere === 'irsaliye' && (
        <IrsaliyeCekmece siparisId={siparis.id} kapat={() => setPencere(null)} acildi={(m) => { setPencere(null); setBilgi(m); yenile(); }} />
      )}
    </div>
  );
}
